import { Component, OnInit } from '@angular/core';
import { SupplierRegionsService } from 'dm-src/app/modules/supplier-regions/supplier-regions.service';
import { ISelectOption } from 'shared';
import { TranslateService } from '@ngx-translate/core';
import { ISupplierRegionOption } from 'dm-src/types/i-supplier-region-option';
import { ModalService } from 'shared';
import { ColorClass } from 'shared';
import { FlashMessageService } from 'shared';
import { DeliveryPlanType } from 'dm-src/types/delivery-plan-type';
import { UnassignedTimeSlotPlan } from 'dm-src/models/unassigned-time-slot-plan';
import { finalize } from 'rxjs/operators';

@Component({
    selector: 'app-supplier-regions',
    templateUrl: './supplier-regions.component.html',
    styleUrls: ['./supplier-regions.component.scss'],
})
export class SupplierRegionsComponent implements OnInit {
    public regionsSettings = {};
    public zipCodesSettings = {};
    public supplierRegions: ISupplierRegionOption[];
    public availableZipCodes: ISelectOption[];
    public ColorClass = ColorClass;
    public regionsModel: ISelectOption[];
    public zipCodesModel: ISelectOption[];
    public isSaveInProgress = false;
    public isRoutesReplanInProgress = false;
    public isUnassignedTimeSlotPlanningInProgress = false;
    public draftDeliveryPlan: any = null;
    public unassignedTimeSlotPlan: UnassignedTimeSlotPlan = null;
    public deliveryDate: string;
    public isDeleteInProgress = false;
    public isSelectedRegionSaved = true;
    public _lat: number;
    public _long: number;
    public _sameDayUtilization: number;
    public _nextDaysUtilization: number;
    public _useDynamicRouting: boolean = false;
    public useCurrentTime: boolean = false;
    public _shiftPickingToDeliveryRatio: number = 0.5;
    public _travelTimeMultiplier: number = 1.0;
    public _deliveryServiceTimeSeconds: number = 0;
    public _deliveryWindowEndToleranceSeconds: number = 0;

    constructor(
        private _translateService: TranslateService,
        private _flashMessageService: FlashMessageService,
        public supplierRegionsService: SupplierRegionsService,
        public modalService: ModalService
    ) {
        this.zipCodesModel = [];
        this.regionsModel = [];
    }

    ngOnInit(): void {
        this.deliveryDate = new Date().toISOString().slice(0, 10);
        this.supplierRegionsService.getSupplierRegionsData();
        this.supplierRegionsService.selectedZipCodes.subscribe(
            (selectedZipCodes) => (this.zipCodesModel = selectedZipCodes)
        );

        this.supplierRegionsService.supplierRegionOptions.subscribe(
            (regions) => (this.supplierRegions = regions)
        );

        this.supplierRegionsService.availableZipCodes.subscribe(
            (availableZipCodes) => (this.availableZipCodes = availableZipCodes)
        );

        this.regionsSettings = this.getRegionsSettings();
        this.zipCodesSettings = this.getZipCodesSettings();
    }

    public onRegionSelect(supplierRegion: ISupplierRegionOption): void {
        this.isSelectedRegionSaved = supplierRegion.id !== undefined;
        this.supplierRegionsService.changeSelectedRegion(supplierRegion.id);
        this._lat = supplierRegion.lat;
        this._long = supplierRegion.long;
        this._sameDayUtilization = supplierRegion.sameDayUtilization;
        this._nextDaysUtilization = supplierRegion.nextDaysUtilization;
        this._useDynamicRouting = supplierRegion.useDynamicRouting;
        this._shiftPickingToDeliveryRatio = supplierRegion.shiftPickingToDeliveryRatio;
        this._travelTimeMultiplier = supplierRegion.travelTimeMultiplier;
        this._deliveryServiceTimeSeconds = supplierRegion.deliveryServiceTimeSeconds;
        this._deliveryWindowEndToleranceSeconds = supplierRegion.deliveryWindowEndToleranceSeconds;

        this.useCurrentTime = false;
        this.unassignedTimeSlotPlan = null;
    }

    public onZipCodesSelect(zipCode: ISelectOption) {
        this.supplierRegionsService.assignZipCode(zipCode);
    }

    public onZipCodeUnassignment(zipCodeID: string) {
        this.supplierRegionsService.unassignZipCode(zipCodeID);
    }

    public onRegionCreated(newRegionName: string) {
        this.supplierRegionsService.addNewSupplierRegion(newRegionName);
    }

    public onRegionsSave() {
        this.isSaveInProgress = true;
        this.supplierRegionsService.saveRegions().subscribe((response) => {
            this.isSaveInProgress = false;
            this.isSelectedRegionSaved = true;
            if (response.status !== 200) {
                this._flashMessageService.showStoredMessage();
            } else if (this.regionsModel[0].id === undefined) {
                this.regionsModel[0].id = response.body.find(
                    (x) => x.regionName === this.regionsModel[0].label
                ).supplierRegionID;
            }
        });
    }

    public onRoutesReplan() {
        if (!this.regionsModel?.length || !this.deliveryDate) {
            return;
        }

        this.isRoutesReplanInProgress = true;

        this.supplierRegionsService
            .replanRoutes(
                this.regionsModel[0].id,
                this.deliveryDate,
                DeliveryPlanType.Draft,
                this.useCurrentTime
            )
            .subscribe((response) => {
                this.isSelectedRegionSaved = true;
                this.isRoutesReplanInProgress = false;

                if (response.status !== 200) {
                    this._flashMessageService.showStoredMessage();
                    return;
                }

                this.draftDeliveryPlan = response.body;

                this.modalService.setModalVisibility(
                    true,
                    'delivery-plan-preview'
                );
            });
    }

    public onPlanUnassignedTimeSlots(): void {
        if (
            !this.regionsModel?.length ||
            !this.deliveryDate ||
            this.isUnassignedTimeSlotPlanningInProgress
        ) {
            return;
        }

        this.isUnassignedTimeSlotPlanningInProgress = true;
        this.unassignedTimeSlotPlan = null;

        this.supplierRegionsService
            .planUnassignedTimeSlots(
                this.regionsModel[0].id,
                this.deliveryDate
            )
            .pipe(
                finalize(() =>
                    this.isUnassignedTimeSlotPlanningInProgress = false
                )
            )
            .subscribe(
                (response) => {
                    if (response.status !== 200) {
                        this._flashMessageService.showStoredMessage();
                        return;
                    }

                    this.unassignedTimeSlotPlan = response.body;
                },
                () => {
                    this._flashMessageService.showStoredMessage();
                }
            );
    }

    public getUnassignedTimeSlotPlanStatusKey(): string {
        const statusKeys: { [status: string]: string } = {
            Completed: 'supplier-regions.unassigned-timeslot-status-completed',
            NoUnassignedOrders:
                'supplier-regions.unassigned-timeslot-status-no-orders',
            NoActiveCouriers:
                'supplier-regions.unassigned-timeslot-status-no-couriers',
            NoCandidateTimeSlots:
                'supplier-regions.unassigned-timeslot-status-no-slots',
            NoSolution:
                'supplier-regions.unassigned-timeslot-status-no-solution',
        };

        return statusKeys[this.unassignedTimeSlotPlan?.status] || '';
    }

    public onPublishDeliveryPlan() {
      this.supplierRegionsService
        .publishDeliveryPlan(
            this.draftDeliveryPlan.regionId,
            this.draftDeliveryPlan.deliveryDate
        )
        .subscribe((response) => {

            if (response.status !== 200) {
                this._flashMessageService.showStoredMessage();
                return;
            }

            this.modalService.setModalVisibility(
                false,
                'delivery-plan-preview');

            this._flashMessageService.showMessage(
                null,
                this._translateService.instant('common.succeeded'),
                10000,
                ColorClass.Info);

            this.draftDeliveryPlan = null;
        });
}

    public onRegionDeleteConfirmed() {
        this.modalService.setModalVisibility(false, 'delete-region-confirmation');
        this.isDeleteInProgress = true;
        this.supplierRegionsService
            .deleteRegion(this.regionsModel[0].id)
            .subscribe((response) => {
                this.isDeleteInProgress = false;
                if (response.status !== 200) {
                    this._flashMessageService.showStoredMessage();
                } else {
                    this.regionsModel = [];
                }
            });
    }

    private getRegionsSettings(): object {
        return {
            enableSearchFilter: true,
            addNewItemOnFilter: true,
            singleSelection: true,
            labelKey: 'label',
            primaryKey: 'id',
            searchBy: ['label'],
            text: this._translateService.instant('supplier-regions.choose-region'),
            searchPlaceholderText: this._translateService.instant('common.search'),
            noDataLabel: this._translateService.instant(
                'supplier-regions.no-region-found'
            ),
        };
    }

    private getZipCodesSettings(): object {
        return {
            singleSelection: false,
            enableSearchFilter: true,
            labelKey: 'label',
            primaryKey: 'id',
            searchBy: ['label'],
            lazyLoading: true,
            badgeShowLimit: 0,
            enableCheckAll: false,
            text: this._translateService.instant('supplier-regions.assign-zip-codes'),
            searchPlaceholderText: this._translateService.instant('common.search'),
            noDataLabel: this._translateService.instant(
                'supplier-regions.no-region-selected'
            ),
        };
    }

    public onChangeLatLon() {
        this.supplierRegionsService.assignLatLong(this._lat, this._long);
    }

    public onChangeUtilizations() {
        this.supplierRegionsService.assignUtilizations(this._sameDayUtilization, this._nextDaysUtilization);
    }

    public onChangeUseDynamicRouting() {
        this.supplierRegionsService.assignUseDynamicRouting(this._useDynamicRouting);
    }

    public onChangeShiftPickingToDeliveryRatio() {
        this.supplierRegionsService.assignShiftPickingToDeliveryRatio(this._shiftPickingToDeliveryRatio);
    }

    public onChangeTravelTimeMultiplier() {
        this.supplierRegionsService.assignTravelTimeMultiplier(this._travelTimeMultiplier);
    }

    public onChangeDeliveryServiceTimeSeconds() {
        this.supplierRegionsService.assignDeliveryServiceTimeSeconds(this._deliveryServiceTimeSeconds);
    }

    public onChangeDeliveryWindowEndToleranceSeconds() {
        this.supplierRegionsService.assignDeliveryWindowEndToleranceSeconds(this._deliveryWindowEndToleranceSeconds);
    }
}
